/**
 * hooks/useVersionGate.ts
 * ------------------------------------------------------------------
 * Version gate (slice 2, layer C). Compares the deployed build id
 * (serverBuild from DataContext) against this bundle's baked build id
 * (via isClientStale), ORs in the service-worker check (lib/swUpdate,
 * which only fires once the page is known to be behind), and drives the
 * automatic reload.
 *
 * AUTO-RELOAD POLICY (Oct 2026) — only at a QUIET MOMENT, as defined in
 * lib/updatePolicy.quietMoment:
 *   - the tab is hidden (nobody is looking at it), or it has had no key,
 *     click, scroll or touch for five minutes — reading is not idle;
 *   - and no form holds unsaved work (DirtyContext);
 *   - and nothing is under way that a reload would interrupt (setReloadHold
 *     — payslips being emailed).
 * It used to reload on the next focus, which is exactly when the person
 * has come back to work; that and a false "new version" after a hard
 * refresh were the reload staff saw. Until a quiet moment comes, the
 * banner stays and the person can reload themselves.
 * ------------------------------------------------------------------
 */
import { useEffect, useState } from 'react';
import { useData } from '@/contexts/DataContext';
import { useDirty } from '@/contexts/DirtyContext';
import { isClientStale } from '@/lib/version';
import { quietMoment } from '@/lib/updatePolicy';
import {
  reloadToLatest, isSwUpdateAvailable, subscribeSwUpdate, noteServerBuild, reloadHeld,
} from '@/lib/swUpdate';

const INPUT_EVENTS = ['keydown', 'pointerdown', 'wheel', 'touchstart'] as const;

export function useVersionGate(): { stale: boolean } {
  const { serverBuild } = useData();
  const { hasUnsavedWork } = useDirty();
  const [swUpdate, setSwUpdate] = useState(isSwUpdateAvailable());
  useEffect(() => subscribeSwUpdate(() => setSwUpdate(true)), []);
  useEffect(() => { noteServerBuild(serverBuild); }, [serverBuild]);
  const stale = isClientStale(serverBuild) || swUpdate;

  useEffect(() => {
    if (!stale) return;
    let lastInput = Date.now();
    let done = false;
    const onInput = () => { lastInput = Date.now(); };
    const maybeReload = () => {
      if (done) return;
      if (quietMoment({
        hidden: document.visibilityState === 'hidden',
        msSinceInput: Date.now() - lastInput,
        unsavedWork: hasUnsavedWork(),
        busy: reloadHeld(),
      })) {
        done = true;
        reloadToLatest();
      }
    };
    INPUT_EVENTS.forEach((e) => window.addEventListener(e, onInput, { passive: true, capture: true }));
    document.addEventListener('visibilitychange', maybeReload);
    const timer = window.setInterval(maybeReload, 30_000);
    return () => {
      INPUT_EVENTS.forEach((e) => window.removeEventListener(e, onInput, { capture: true }));
      document.removeEventListener('visibilitychange', maybeReload);
      window.clearInterval(timer);
    };
  }, [stale, hasUnsavedWork]);

  return { stale };
}
